import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * One-stop per-route <title> manager. Rendered once inside App.js (below
 * the Router, above Routes) so every route's title stays in sync with
 * the URL. A route can override with a more specific title by calling
 * `useDocumentTitle()` deep in the tree.
 *
 * Pattern: "{page} · Chat With Menu". Tabs that look static (pricing,
 * about) get a short page name; deep-linked pages (restaurant/:id,
 * dish/:id) keep the base title since the component itself knows the
 * name and can call useDocumentTitle to specialize.
 */

const BRAND = 'Chat With Menu';

// Map any pathname → human page label. Order matters: the first match
// wins. Use patterns with parameter prefixes before the static match.
const ROUTE_TITLES = [
  { match: (p) => p === '/unified' || p === '/unified/',                   label: null }, // hero — no prefix
  { match: (p) => p === '/unified/discover',                                label: 'Discover' },
  { match: (p) => p === '/unified/for-restaurants',                         label: 'For Business' },
  { match: (p) => p === '/unified/pricing',                                 label: 'Pricing' },
  { match: (p) => p === '/unified/restaurant-pricing',                      label: 'Pricing for restaurants' },
  { match: (p) => p === '/unified/dashboard',                               label: 'Dashboard' },
  { match: (p) => p === '/unified/chat',                                    label: 'Chat' },
  { match: (p) => p === '/unified/loyalty',                                 label: 'Rewards' },
  { match: (p) => p === '/unified/my-table',                                label: 'My Table' },
  { match: (p) => p === '/unified/family-management',                       label: 'Family' },
  { match: (p) => p === '/unified/notifications',                           label: 'Notifications' },
  { match: (p) => p === '/unified/billing',                                 label: 'Billing' },
  { match: (p) => p === '/unified/settings',                                label: 'Settings' },
  { match: (p) => p === '/unified/onboarding',                              label: 'Get started' },
  { match: (p) => p === '/unified/add-review',                              label: 'Write a review' },
  { match: (p) => p === '/unified/owner-dashboard',                         label: 'Owner Dashboard' },
  { match: (p) => p === '/unified/menu-manager',                            label: 'Menu Manager' },
  { match: (p) => p === '/unified/menu-manager-legacy',                     label: 'Menu Manager · Advanced' },
  { match: (p) => p === '/unified/server-dashboard',                        label: 'Service Tools' },
  { match: (p) => p === '/unified/add-restaurant',                          label: 'Add Restaurant' },
  { match: (p) => p === '/unified/checkout',                                label: 'Checkout' },
  { match: (p) => p === '/unified/order-history',                           label: 'Order History' },
  { match: (p) => p.startsWith('/unified/order-status/'),                   label: 'Order Status' },
  { match: (p) => p === '/unified/safety-signals',                          label: 'Safety Signals' },
  { match: (p) => p === '/unified/table-questions',                         label: 'Table Questions' },
  { match: (p) => p === '/unified/terms',                                   label: 'Terms' },
  { match: (p) => p === '/unified/privacy-policy',                          label: 'Privacy Policy' },
  { match: (p) => p === '/unified/how-it-works',                            label: 'How it works' },
  { match: (p) => p === '/unified/about',                                   label: 'About' },
  { match: (p) => p === '/unified/qr',                                      label: 'QR Codes' },
  { match: (p) => p.startsWith('/unified/restaurant/'),                     label: null }, // component owns title
  { match: (p) => p.startsWith('/unified/dish/'),                           label: null }, // component owns title
];

function titleFor(pathname) {
  const hit = ROUTE_TITLES.find((r) => r.match(pathname));
  if (!hit || !hit.label) return BRAND;
  return `${hit.label} · ${BRAND}`;
}

export default function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    try { document.title = titleFor(pathname); } catch {}
  }, [pathname]);
  return null;
}

/**
 * Screens that know a runtime name (e.g. "Trattoria Giulia · Chat With
 * Menu" on /unified/restaurant/42) can call this from deep in the tree.
 * Passing `null` or an empty string resets to just the brand.
 */
export function useDocumentTitle(title) {
  useEffect(() => {
    if (!title) { try { document.title = BRAND; } catch {}; return; }
    try { document.title = `${title} · ${BRAND}`; } catch {}
  }, [title]);
}
